import { useSendGoogleDriveFilteToTelegram } from '@/shared/api'
import {
    DocumentIcon,
    GoogleDriveGraySVGIcon,
    ShareSVGIcon,
    TrashIcon,
} from '@/shared/assets'
import { cn } from '@/true-fsd/shared/utils/css'
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    ShadcnButton,
} from '@/shared/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { DocumentRecord } from '@synapex/prisma/client'
import { Check, EllipsisVerticalIcon, Loader2, Plus, XIcon } from 'lucide-react'
import { useRef, useState } from 'react'

export function UploadFileFiled({
    onDeleteItem,
    onFileChange,
    disabled,
    error,
    placeholder,
    selectedItem,
    setSelectedItem,
    isLoading,
    className,
}: {
    onDeleteItem: (contract: DocumentRecord) => void
    onFileChange: (file: File) => void
    disabled?: boolean
    error?: boolean
    placeholder: string
    selectedItem: DocumentRecord | null
    setSelectedItem: (item: DocumentRecord | null) => void
    isLoading?: boolean
    className?: string
}) {
    const [open, setOpen] = useState(false)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

    const sendToTelegram = useSendGoogleDriveFilteToTelegram()

    const fileInputRef = useRef<HTMLInputElement>(null)

    function handleCancelDelete(event: React.MouseEvent) {
        event.stopPropagation()
        setShowDeleteConfirmation(false)
    }

    function handleDeleteConfirmation() {
        if (!showDeleteConfirmation) {
            setShowDeleteConfirmation(true)
        }
    }

    function handleDelete() {
        onDeleteItem(selectedItem!)
        setSelectedItem(null)
        setShowDeleteConfirmation(false)
    }

    function handleSendToSelf(document: DocumentRecord) {
        setOpen(false)
        sendToTelegram.mutate(document.documentRecordId)
    }

    function handleOpenGoogleDriveFolder(document: DocumentRecord) {
        if (document.unsignedLink) {
            window.open(document.unsignedLink, '_blank')
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        onFileChange(file)
    }

    const finalLoadingStatus = isLoading || sendToTelegram.isPending

    if (!selectedItem) {
        return (
            <div
                className={cn(
                    'flex w-52 items-center justify-between rounded-[5px] border border-dashed px-[10px] py-2 text-xs',
                    !disabled || finalLoadingStatus
                        ? 'cursor-pointer border-blue-primary text-black'
                        : 'cursor-not-allowed text-secondary',
                    error ? 'border-red-500' : '',
                    className
                )}
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={disabled || finalLoadingStatus}
                />
                {finalLoadingStatus ? (
                    <>
                        <span className="overflow-hidden text-ellipsis text-nowrap">
                            Загрузка...
                        </span>
                        <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
                    </>
                ) : (
                    <>
                        <span className="overflow-hidden text-ellipsis text-nowrap">
                            {placeholder}
                        </span>
                        <div className="h-3 w-3">
                            <Plus className="h-3 w-3" />
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild disabled={disabled || finalLoadingStatus}>
                <ShadcnButton
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'flex h-auto items-center justify-between px-[10px] py-2 text-xs font-normal',
                        className
                    )}
                >
                    <div className="flex gap-1 overflow-hidden">
                        <img src={DocumentIcon} className="h-4 w-3 shrink-0" />
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {selectedItem.fileName}
                        </span>
                    </div>
                    {finalLoadingStatus ? (
                        <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                        <EllipsisVerticalIcon className="ml-2 h-4 w-4 shrink-0" />
                    )}
                </ShadcnButton>
            </PopoverTrigger>
            <PopoverContent
                className={cn('w-52 p-0', className)}
                align="start"
                sideOffset={4}
                style={{ maxHeight: '200px', overflow: 'scroll' }}
            >
                <Command shouldFilter={false}>
                    <CommandList className="max-h-[300px] overflow-y-scroll rounded-lg p-2">
                        <CommandGroup className="p-0">
                            <CommandItem
                                className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                                onSelect={() => handleSendToSelf(selectedItem)}
                            >
                                <span>Отправить себе в PDF</span>
                                <ShareSVGIcon className="stroke-blue-primary" />
                            </CommandItem>
                            <CommandItem
                                className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                                onSelect={() =>
                                    handleOpenGoogleDriveFolder(selectedItem)
                                }
                            >
                                <span>Ссылка на документ</span>
                                <GoogleDriveGraySVGIcon />
                            </CommandItem>
                            <CommandItem
                                onSelect={handleDeleteConfirmation}
                                className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                            >
                                <span>Удалить</span>
                                {showDeleteConfirmation ? (
                                    <div className="flex justify-between">
                                        <div onClick={handleDelete}>
                                            <Check className="ml-2 cursor-pointer text-[#056FFF]" />
                                        </div>
                                        <div onClick={handleCancelDelete}>
                                            <XIcon className="ml-2 cursor-pointer" />
                                        </div>
                                    </div>
                                ) : (
                                    <img src={TrashIcon} />
                                )}
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
